import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/outline";
import { MdDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { IMedia } from "../../../../model/unityObject";
import ImageCard from "../../../gallery/components/ImageCard";
import InfoIconWithTooltip from "../../../../common/components/Icon/InfoIconWithTooltip";

// The avatar track's PDF context-document lists (2026-07-16 AI design pass):
//
// variant "pdf-items" — content-avatar-ai-persona.pdf_items (JSON column):
//   [{ source_media_id, description, is_visible_to_avatar, ...passthrough }]
//   • description          — free text fed verbatim into the persona's system
//                            prompt ("DOCUMENTS DISPONIBLES" section)
//   • is_visible_to_avatar — true: the avatar's role knows the document during
//                            the dialogue; false: used only to evaluate
//                            success/failure + debriefing (avatar-ai.js).
//                            Backend defaults to true when absent.
//
// variant "media-list" — content-avatar-ai.source_medias (many-relation):
//   plain number[] of source-media ids (NodeForm's catalog seeding normalizes
//   populated objects to ids). The PUT sets the relation from the id array.
//
// Both lists are STRUCTURAL (no PLD projection): the whole editor is locked
// outside the source language. VR parity: both VR editors add PDFs through
// their ItemSetFile list (ValueMediaType.pdf); no count bounds exist there —
// the only VR limit is a total-transcription char cap enforced at play time,
// which the web cannot mirror (transcriptions aren't available at edit time).
//
// The array is written straight back to react-hook-form; the existing save
// path round-trips it on the content PUT (source language only).

export interface PdfItem {
  source_media_id: number;
  description?: string;
  is_visible_to_avatar?: boolean;
  [key: string]: unknown;
}

const NOMEDIA_FALLBACK: IMedia = {
  createdAt: "",
  id: -1,
  name: "",
  label: "",
  organization: 3,
  s3_url: "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/NOMEDIA.png",
  type: null,
  updatedAt: "",
  filename: "NOMEDIA.png",
  tags: [],
  upload_status: "completed",
} as unknown as IMedia;

interface PdfItemsFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** Which list shape this instance edits (see header comment). */
  variant: "pdf-items" | "media-list";
  /** RHF field name: "pdf_items" (persona) or "source_medias" (avatar-ai). */
  fieldName: string;
  /** Full media library, to resolve each row's id to a card. */
  mediasList: IMedia[];
  /** Opens the shared gallery modal (pdf mode); index -1 appends a new row. */
  onPickPdf: (index: number) => void;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** The whole list is structural — editable only in the source language. */
  isSourceLanguage: boolean;
}

const PdfItemsField = ({
  control,
  setValue,
  label,
  variant,
  fieldName,
  mediasList,
  onPickPdf,
  disabled = false,
  isSourceLanguage,
}: PdfItemsFieldProps) => {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: fieldName });
  const isPdfItems = variant === "pdf-items";

  // Normalized display rows: always { source_media_id, ... } internally; the
  // media-list variant projects back to plain ids on write.
  const rows: PdfItem[] = Array.isArray(watched)
    ? watched.map((r) =>
        r && typeof r === "object"
          ? (r as PdfItem)
          : ({ source_media_id: Number(r) } as PdfItem),
      )
    : [];

  const structureLocked = disabled || !isSourceLanguage;

  const write = (next: PdfItem[]) =>
    setValue(
      fieldName,
      isPdfItems ? next : next.map((r) => r.source_media_id),
      { shouldDirty: true },
    );

  const handleDescriptionChange = (index: number, value: string) =>
    write(rows.map((r, i) => (i === index ? { ...r, description: value } : r)));

  const handleToggleVisible = (index: number, checked: boolean) =>
    write(rows.map((r, i) => (i === index ? { ...r, is_visible_to_avatar: checked } : r)));

  const handleRemove = (index: number) => write(rows.filter((_, i) => i !== index));

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <button
          type="button"
          onClick={() => onPickPdf(-1)}
          disabled={structureLocked}
          className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          <span>{t("pages.project.editNode.pdfItemsAdd")}</span>
        </button>
      </div>

      {structureLocked && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.sourceLanguageOnlyHint")}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="text-xs italic text-gray-500 border border-gray-200 rounded-lg p-3">
          {t("pages.project.editNode.pdfItemsEmpty")}
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, index) => {
            const media =
              mediasList.find((m) => Number(m.id) === Number(row.source_media_id)) ??
              NOMEDIA_FALLBACK;
            return (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-600">
                    {t("pages.project.editNode.pdfItemsItemLabel", {
                      n: index + 1,
                      defaultValue: `Document ${index + 1}`,
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={structureLocked}
                    className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={t("general.delete") as string}
                  >
                    <MdDeleteOutline size={20} />
                  </button>
                </div>

                <div className="flex flex-wrap gap-4">
                  <div className="w-40 shrink-0">
                    <ImageCard
                      media={media}
                      currentMedia={
                        Number(row.source_media_id) > 0
                          ? Number(row.source_media_id)
                          : undefined
                      }
                      isMediaPickerMode={false}
                      showThreeDotsMenu={false}
                      disabled={structureLocked}
                      handleButtonClick={() => onPickPdf(index)}
                      handleCardClick={() => {
                        if (!structureLocked) onPickPdf(index);
                      }}
                      className="w-40"
                    />
                  </div>

                  {isPdfItems && (
                    <div className="flex-1 min-w-[12rem] flex flex-col gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("pages.project.editNode.pdfItemsDescription")}
                        </label>
                        <textarea
                          value={row.description ?? ""}
                          onChange={(e) => handleDescriptionChange(index, e.target.value)}
                          disabled={structureLocked}
                          rows={3}
                          className="dynamic-field appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        />
                      </div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          // Backend semantics: absent ⇒ visible (avatar-ai.js
                          // defaults to true) — mirror that here.
                          checked={row.is_visible_to_avatar !== false}
                          onChange={(e) => handleToggleVisible(index, e.target.checked)}
                          disabled={structureLocked}
                          className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
                        />
                        <span className="flex items-center gap-1 text-xs text-gray-600">
                          {t("pages.project.editNode.pdfItemsVisibleToAvatar")}
                          <InfoIconWithTooltip
                            message={t("pages.project.editNode.pdfItemsVisibleToAvatarHint")}
                          />
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PdfItemsField;
